import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { callApiAsync, getFacturacionReportExport } from "../../services/apiService";
import ButtonComponent from "../../components/button";
import SelectInput from "../../components/selectInput";
import ToastNotification from "../../components/toastNotification";
import { Grid } from "@mui/material";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const MESES = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

const Contador = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const [loading, setLoading] = useState(false);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => ({
    value: y,
    label: String(y),
  }));

  const monthOptions = MESES.map((m) => ({ value: m.value, label: m.label }));

  const { control, watch } = useForm({
    defaultValues: { month: currentMonth, year: currentYear },
  });
  const month = watch("month");
  const year = watch("year");

  const buildExcelFromReport = (rows) => {
    const header = [
      "Unidad facturación",
      "Laboratorio",
      "Paquete",
      "Descripción",
      "Producto principal",
      "Valor Pesos",
      "Valor USD",
      "Usuarios",
    ];
    const data = [header];

    rows.forEach((row) => {
      const emails = row.userEmails || [];
      // Primera fila: siempre descripción del paquete (o temporal) y valor; sin usuario
      data.push([
        row.unidadFacturacion || "",
        row.laboratorio || "",
        row.paquete || "",
        row.descripcion || "",
        row.productoPrincipal || "",
        row.valorPesos || "",
        row.valorUSD || "",
        "",
      ]);
      // Filas siguientes: un usuario por fila (descripción, producto principal y valor en blanco)
      emails.forEach((email) => {
        data.push([
          row.unidadFacturacion || "",
          row.laboratorio || "",
          row.paquete || "",
          "",
          "",
          "",
          "",
          email || "",
        ]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(data);
    const headerStyle = {
      fill: { fgColor: { rgb: "FF1E3A5F" }, patternType: "solid" },
      font: { bold: true, color: { rgb: "FFFFFFFF" } },
    };
    ["A", "B", "C", "D", "E", "F", "G", "H"].forEach((col) => {
      const ref = `${col}1`;
      if (ws[ref]) ws[ref].s = headerStyle;
    });
    ws["!cols"] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 45 },
      { wch: 60 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 40 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturación");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `facturacion_paquetes_${year}_${String(month).padStart(2, "0")}.xlsx`);
  };

  const handleExport = () => {
    const mes = month != null && month !== "" ? Number(month) : currentMonth;
    const anio = year != null && year !== "" ? Number(year) : currentYear;

    if (!mes || mes < 1 || mes > 12 || !anio) {
      setToastSeverity("warning");
      setToastMessage("Seleccione mes y año.");
      setToastOpen(true);
      return;
    }

    setLoading(true);
    callApiAsync(
      () => getFacturacionReportExport(mes, anio),
      (data) => {
        setLoading(false);
        if (Array.isArray(data) && data.length === 0) {
          setToastSeverity("info");
          setToastMessage("No hay datos de facturación para el periodo seleccionado.");
          setToastOpen(true);
          return;
        }
        if (Array.isArray(data)) {
          buildExcelFromReport(data);
          setToastSeverity("success");
          setToastMessage("Excel exportado correctamente.");
          setToastOpen(true);
        } else {
          setToastSeverity("error");
          setToastMessage("No se recibieron datos válidos.");
          setToastOpen(true);
        }
      },
      (err) => {
        setLoading(false);
        const msg = err?.response?.data?.error || err?.message || "Error al exportar.";
        setToastSeverity("error");
        setToastMessage(msg);
        setToastOpen(true);
      }
    );
  };

  return (
    <div>
      <h1>Reporte facturación (Contador)</h1>
      <p style={{ marginBottom: 16 }}>
        Seleccione mes y año para exportar todos los pricing instances facturados en ese periodo a Excel.
      </p>
      <Grid container spacing={3} alignItems="center" sx={{ marginTop: 1 }}>
        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ paddingRight: { xs: 0, md: 2 } }}>
          <SelectInput
            name="month"
            control={control}
            label="Mes"
            options={monthOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ paddingRight: { xs: 0, md: 2 } }}>
          <SelectInput
            name="year"
            control={control}
            label="Año"
            options={yearOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ paddingLeft: { md: 1 } }}>
          <ButtonComponent
            type="button"
            onClick={handleExport}
            disabled={loading}
            backgroundColor="#1976d2"
            textColor="#fff"
          >
            {loading ? "Exportando…" : "Exportar Excel"}
          </ButtonComponent>
        </Grid>
      </Grid>

      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </div>
  );
};

export default Contador;
